import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Calendar, MapPin, Hotel, ListChecks, Plane, Check, X, Clock, Trash2 } from "lucide-react";

const AVAIL = {
  accepted: { label: "Accepted", cls: "bg-emerald-600 text-white", icon: Check },
  declined: { label: "Declined", cls: "bg-red-500 text-white", icon: X },
  awaiting: { label: "Awaiting", cls: "bg-amber-100 text-amber-800 border border-amber-300", icon: Clock },
};

export default function AdminTournamentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [teams, setTeams] = useState([]);

  const load = useCallback(() => {
    api.get(`/admin/tournaments/${id}`).then(({ data }) => setData(data)).catch((e) => {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
      setData({ tournament: null, roster: [], summary: {} });
    });
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/teams").then(({ data }) => setTeams(data.items || [])).catch(() => {}); }, []);

  const setAvailability = async (r, status) => {
    try {
      await api.patch(`/admin/tournaments/${id}/availability/${r.player_id}`, { status });
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const toggleTeam = async (teamId) => {
    const cur = data.tournament.team_ids || [];
    const next = cur.includes(teamId) ? cur.filter((x) => x !== teamId) : [...cur, teamId];
    try {
      await api.patch(`/admin/tournaments/${id}`, { team_ids: next });
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this tournament?")) return;
    try { await api.delete(`/admin/tournaments/${id}`); window.location.href = "/admin/tournaments"; }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  if (!data) return <AdminLayout><div className="p-8 label-overline">Loading tournament…</div></AdminLayout>;
  const t = data.tournament || {};
  const s = data.summary || {};
  const assigned = t.team_ids || [];

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl">
        <Link to="/admin/tournaments" className="label-overline text-slate-500 hover:text-[#E63946]">← All Tournaments</Link>
        <div className="flex items-start justify-between gap-4 mt-3">
          <div>
            <div className="flex items-center gap-2 label-overline text-[#E63946]"><Trophy className="w-4 h-4" /> CBR Nation</div>
            <h1 data-testid="tournament-title" className="font-display text-4xl uppercase tracking-tighter mt-2">{t.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-slate-500">
              {(t.start_date || t.end_date) && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {[t.start_date, t.end_date].filter(Boolean).join(" → ")}</span>}
              {t.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {t.location}</span>}
              {t.venue && <span>{t.venue}</span>}
            </div>
          </div>
          <button data-testid="delete-tournament" onClick={remove} className="inline-flex items-center gap-1 border border-red-200 text-red-600 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-red-50">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>

        {/* Info cards */}
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <InfoCard icon={Hotel} label="Hotel" value={t.hotel_info} />
          <InfoCard icon={ListChecks} label="Schedule" value={t.schedule} />
          <InfoCard icon={Plane} label="Travel Notes" value={t.travel_notes} />
        </div>

        {/* Team assignment */}
        <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5">
          <div className="label-overline text-[10px] mb-3">Assigned Teams</div>
          <div className="flex flex-wrap gap-2">
            {teams.map((tm) => (
              <button key={tm.id} data-testid={`toggle-team-${tm.id}`} onClick={() => toggleTeam(tm.id)}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${assigned.includes(tm.id) ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white border-slate-300 text-slate-600"}`}>
                {tm.name}
              </button>
            ))}
            {teams.length === 0 && <span className="text-xs text-slate-400">No teams available.</span>}
          </div>
        </div>

        {/* Availability summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Tile testid="avail-total" label="Roster" val={s.total || 0} cls="text-[#0A192F]" />
          <Tile testid="avail-accepted" label="Accepted" val={s.accepted || 0} cls="text-emerald-700" />
          <Tile testid="avail-declined" label="Declined" val={s.declined || 0} cls="text-red-600" />
          <Tile testid="avail-awaiting" label="Awaiting" val={s.awaiting || 0} cls="text-amber-700" />
        </div>

        {/* Availability table */}
        <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-3 label-overline">Player</th>
                <th className="text-left p-3 label-overline">Team</th>
                <th className="text-left p-3 label-overline">Pos</th>
                <th className="text-left p-3 label-overline">Availability</th>
                <th className="text-right p-3 label-overline">Set</th>
              </tr>
            </thead>
            <tbody>
              {data.roster.map((r) => {
                const p = r.player || {};
                const meta = AVAIL[r.availability] || AVAIL.awaiting;
                return (
                  <tr key={r.player_id} data-testid={`avail-row-${r.player_id}`} className="border-b border-slate-100 last:border-0">
                    <td className="p-3 font-semibold">{p.first_name} {p.last_name}</td>
                    <td className="p-3 text-xs text-slate-500">{r.team_name}</td>
                    <td className="p-3 font-data">{p.primary_position || "—"}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${meta.cls}`}>
                        <meta.icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <Btn testid={`avail-accept-${r.player_id}`} active={r.availability === "accepted"} activeCls="bg-emerald-600 text-white" onClick={() => setAvailability(r, "accepted")}>Accept</Btn>
                        <Btn testid={`avail-decline-${r.player_id}`} active={r.availability === "declined"} activeCls="bg-red-500 text-white" onClick={() => setAvailability(r, "declined")}>Decline</Btn>
                        <Btn testid={`avail-await-${r.player_id}`} active={r.availability === "awaiting"} activeCls="bg-amber-500 text-white" onClick={() => setAvailability(r, "awaiting")}>Awaiting</Btn>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {data.roster.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-sm text-slate-500">No players. Assign one or more teams above to populate the availability list.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center gap-2 label-overline text-[10px]"><Icon className="w-4 h-4 text-[#E63946]" /> {label}</div>
      <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{value || <span className="text-slate-400">—</span>}</p>
    </div>
  );
}

function Tile({ label, val, cls, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 p-4 rounded-sm">
      <div className="label-overline">{label}</div>
      <div className={`font-data text-3xl mt-1 ${cls}`}>{val}</div>
    </div>
  );
}

function Btn({ children, onClick, active, activeCls = "bg-[#0A192F] text-white", testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${active ? activeCls : "bg-white border border-slate-300 text-[#0A192F] hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}
